import React, { useState, useEffect, useRef, useMemo } from 'react';
import { SpotVisit, CampingSpot, AppUser, Vehicle } from '../../types';
import { updateUserSettings, sendStickerBoardEmail, fetchSpotsByIds } from '../../services/firebaseService';
import { subscribeToVehicles } from '../../services/vehicleService';
import { MapPin, RotateCw, Trash2, Save, Sparkles, AlertCircle, Compass, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import { jsPDF } from 'jspdf';
import { reverseGeocode } from '../../services/geocodingService';
import { getRetroColors, getRetroShape, createSeededRandom } from '../../utils/spotVisuals';

const LANGUAGE_TO_COUNTRY_CODE: Record<string, string> = {
  no: 'NO', en: 'GB', de: 'DE', pl: 'PL', es: 'ES', fr: 'FR', nl: 'NL',
};

function getCountryCodeFromLanguage(lang?: string | null): string {
  if (!lang) return 'NO';
  return LANGUAGE_TO_COUNTRY_CODE[lang] || 'EU';
}

function getUserInitials(name?: string | null, email?: string | null): string {
  const source = (name && name.trim()) || (email && email.split('@')[0]) || '';
  if (!source) return '?';
  const parts = source.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const EuStarsRing: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    {Array.from({ length: 12 }).map((_, i) => {
      const angle = (i / 12) * Math.PI * 2 - Math.PI / 2;
      const r = 9;
      const cx = 12 + r * Math.cos(angle);
      const cy = 12 + r * Math.sin(angle);
      return <circle key={i} cx={cx} cy={cy} r={1} fill="#FFCC00" />;
    })}
  </svg>
);

interface StickerLayoutItem {
  x: number; // 0-100% of container width
  y: number; // 0-100% of container height
  rotation: number; // degrees
  scale: number; // 0.5 to 1.5
}

interface StickerBoardProps {
  visits: SpotVisit[];
  spots: CampingSpot[];
  user: AppUser;
}

// Deterministiske farger/former/seed er delt med VisitsTab og reise-replayen —
// se utils/spotVisuals.ts (eng-review D5). Regresjonstest låser at fargene er
// identiske med den gamle inline-implementasjonen.

function drawMiniMap(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  spotId: string,
  colors: any
) {
  ctx.save();
  ctx.translate(cx, cy);

  // Clip to circular map inset
  ctx.beginPath();
  ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
  ctx.clip();

  const rand = createSeededRandom(spotId);

  // 1. Draw Map Background (parchment/light tint based on colors)
  let mapBg = '#fbfaf5';
  let mapGridColor = 'rgba(0, 0, 0, 0.08)';
  let mapLineColor = 'rgba(0, 0, 0, 0.25)';
  let waterColor = '#e2e8f0';
  let roadColor = 'rgba(220, 38, 38, 0.4)';
  
  if (colors.label === 'amber') {
    mapBg = '#fefaf0';
    mapGridColor = 'rgba(120, 53, 4, 0.07)';
    mapLineColor = 'rgba(120, 53, 4, 0.25)';
    waterColor = '#f0e5d0';
    roadColor = 'rgba(180, 83, 9, 0.35)';
  } else if (colors.label === 'emerald') {
    mapBg = '#f4f9f4';
    mapGridColor = 'rgba(5, 46, 22, 0.06)';
    mapLineColor = 'rgba(5, 46, 22, 0.22)';
    waterColor = '#e0eae0';
    roadColor = 'rgba(4, 120, 87, 0.3)';
  } else if (colors.label === 'rust') {
    mapBg = '#faf5f5';
    mapGridColor = 'rgba(69, 10, 10, 0.06)';
    mapLineColor = 'rgba(69, 10, 10, 0.25)';
    waterColor = '#ebdcdc';
    roadColor = 'rgba(194, 65, 12, 0.35)';
  } else if (colors.label === 'teal') {
    mapBg = '#f2f8f8';
    mapGridColor = 'rgba(4, 47, 46, 0.06)';
    mapLineColor = 'rgba(4, 47, 46, 0.22)';
    waterColor = '#d9e8e8';
    roadColor = 'rgba(13, 148, 136, 0.35)';
  } else if (colors.label === 'burgundy') {
    mapBg = '#faf4f6';
    mapGridColor = 'rgba(76, 5, 25, 0.06)';
    mapLineColor = 'rgba(76, 5, 25, 0.25)';
    waterColor = '#ebdbe1';
    roadColor = 'rgba(190, 24, 93, 0.3)';
  } else if (colors.label === 'slate') {
    mapBg = '#f6f6f6';
    mapGridColor = 'rgba(28, 25, 23, 0.06)';
    mapLineColor = 'rgba(28, 25, 23, 0.22)';
    waterColor = '#e3e3e3';
    roadColor = 'rgba(82, 82, 82, 0.3)';
  }

  ctx.fillStyle = mapBg;
  ctx.fillRect(-size/2, -size/2, size, size);

  // 2. Draw procedural water body (e.g. lake or shoreline)
  ctx.fillStyle = waterColor;
  ctx.beginPath();
  const startX = -size / 2 - 10;
  const startY = (rand() - 0.5) * (size * 0.6);
  ctx.moveTo(startX, startY);
  
  const cp1x = (rand() - 0.2) * (size * 0.5);
  const cp1y = (rand() - 0.5) * (size * 0.8);
  const cp2x = (rand() - 0.5) * (size * 0.5);
  const cp2y = (rand() - 0.2) * (size * 0.8);
  const endX = size / 2 + 10;
  const endY = (rand() - 0.5) * (size * 0.6);

  ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, endX, endY);
  if (rand() > 0.5) {
    ctx.lineTo(size / 2 + 10, size / 2 + 10);
    ctx.lineTo(-size / 2 - 10, size / 2 + 10);
  } else {
    ctx.lineTo(size / 2 + 10, -size / 2 - 10);
    ctx.lineTo(-size / 2 - 10, -size / 2 - 10);
  }
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = mapLineColor;
  ctx.lineWidth = 1;
  ctx.stroke();

  // 3. Draw grid lines (dotted lat/lng)
  ctx.strokeStyle = mapGridColor;
  ctx.lineWidth = 0.75;
  ctx.setLineDash([2, 3]);
  ctx.beginPath();
  ctx.moveTo(-size/4, -size/2); ctx.lineTo(-size/4, size/2);
  ctx.moveTo(size/4, -size/2); ctx.lineTo(size/4, size/2);
  ctx.moveTo(-size/2, -size/4); ctx.lineTo(size/2, -size/4);
  ctx.moveTo(-size/2, size/4); ctx.lineTo(size/2, size/4);
  ctx.stroke();
  ctx.setLineDash([]);

  // 4. Draw topographic contour lines (nested organic shapes)
  ctx.strokeStyle = mapGridColor;
  ctx.lineWidth = 0.8;
  const hillCenterX = (rand() - 0.5) * (size * 0.4);
  const hillCenterY = (rand() - 0.5) * (size * 0.4);
  ctx.beginPath();
  ctx.arc(hillCenterX, hillCenterY, size * 0.25, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(hillCenterX, hillCenterY, size * 0.14, 0, Math.PI * 2);
  ctx.stroke();

  // 5. Draw procedural road / trail (curved line)
  ctx.strokeStyle = roadColor;
  ctx.lineWidth = 1.8;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  const roadStartX = (rand() - 0.5) * size;
  const roadStartY = -size / 2 - 5;
  const roadEndX = (rand() - 0.5) * size;
  const roadEndY = size / 2 + 5;
  ctx.moveTo(roadStartX, roadStartY);
  ctx.quadraticCurveTo(
    (rand() - 0.5) * (size * 0.3),
    (rand() - 0.5) * (size * 0.3),
    roadEndX, roadEndY
  );
  ctx.stroke();
  ctx.setLineDash([]);

  // 6. Draw center target marker (map pin or crosshair)
  ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetY = 2;

  ctx.fillStyle = '#dc2626';
  ctx.strokeStyle = '#fcfbf7';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(-size * 0.08, -size * 0.08, -size * 0.08, -size * 0.18, 0, -size * 0.24);
  ctx.bezierCurveTo(size * 0.08, -size * 0.24, size * 0.08, -size * 0.18, 0, -size * 0.08);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(0, -size * 0.16, size * 0.04, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

const MiniMapThumbnail: React.FC<{
  spotId: string;
  colors: any;
  size: number;
}> = ({ spotId, colors, size }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);
    
    drawMiniMap(ctx, size / 2, size / 2, size, spotId, colors);
  }, [spotId, colors, size]);
  
  return (
    <canvas
      ref={canvasRef}
      style={{ width: `${size}px`, height: `${size}px` }}
      className="rounded-full shadow-inner border border-white/60 shrink-0"
    />
  );
};

function drawActualMap(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  lat: number,
  lng: number,
  tiles: HTMLImageElement[],
  colors: any
) {
  ctx.save();
  ctx.translate(cx, cy);

  // Clip to circular map inset
  ctx.beginPath();
  ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
  ctx.clip();

  // 1. Calculate drawing offsets
  const zoom = 14;
  const numTiles = Math.pow(2, zoom);
  const tileXDouble = ((lng + 180) / 360) * numTiles;
  const latRad = (lat * Math.PI) / 180;
  const tileYDouble = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * numTiles;

  const tileX = Math.floor(tileXDouble);
  const tileY = Math.floor(tileYDouble);

  const offsetX = (tileXDouble - tileX) * 256;
  const offsetY = (tileYDouble - tileY) * 256;

  const adjX = offsetX < 128 ? tileX - 1 : tileX + 1;
  const adjY = offsetY < 128 ? tileY - 1 : tileY + 1;

  const xCoords = [Math.min(tileX, adjX), Math.max(tileX, adjX)];
  const yCoords = [Math.min(tileY, adjY), Math.max(tileY, adjY)];

  // Draw 2x2 grid of tiles centered on (offsetX, offsetY)
  const startX = -offsetX - (tileX === xCoords[0] ? 0 : 256);
  const startY = -offsetY - (tileY === yCoords[0] ? 0 : 256);

  ctx.drawImage(tiles[0], startX, startY, 256, 256);
  ctx.drawImage(tiles[1], startX + 256, startY, 256, 256);
  ctx.drawImage(tiles[2], startX, startY + 256, 256, 256);
  ctx.drawImage(tiles[3], startX + 256, startY + 256, 256, 256);

  // 2. Tint map using multiply composite operation
  let mapTintBg = 'rgba(251, 250, 245, 0.9)'; // default
  if (colors.label === 'amber') {
    mapTintBg = 'rgba(254, 250, 240, 0.85)';
  } else if (colors.label === 'emerald') {
    mapTintBg = 'rgba(244, 249, 244, 0.82)';
  } else if (colors.label === 'rust') {
    mapTintBg = 'rgba(250, 245, 245, 0.85)';
  } else if (colors.label === 'teal') {
    mapTintBg = 'rgba(242, 248, 248, 0.82)';
  } else if (colors.label === 'burgundy') {
    mapTintBg = 'rgba(254, 244, 246, 0.85)';
  } else if (colors.label === 'slate') {
    mapTintBg = 'rgba(246, 246, 246, 0.82)';
  }

  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = mapTintBg;
  ctx.fillRect(-size/2, -size/2, size, size);

  // Reset composite operation for overlay drawing
  ctx.globalCompositeOperation = 'source-over';

  // 3. Draw thin grid border inside
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.15)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(0, 0, size / 2 - 0.5, 0, Math.PI * 2);
  ctx.stroke();

  // 4. Draw red marker pin exactly at (0, 0)
  ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetY = 2;

  ctx.fillStyle = '#dc2626';
  ctx.strokeStyle = '#fcfbf7';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(-size * 0.08, -size * 0.08, -size * 0.08, -size * 0.18, 0, -size * 0.24);
  ctx.bezierCurveTo(size * 0.08, -size * 0.24, size * 0.08, -size * 0.18, 0, -size * 0.08);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Pin center dot
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(0, -size * 0.16, size * 0.04, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

const ActualMiniMap: React.FC<{
  lat: number;
  lng: number;
  spotId: string;
  colors: any;
  size: number;
}> = ({ lat, lng, spotId, colors, size }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tiles, setTiles] = useState<HTMLImageElement[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const zoom = 14;
    const numTiles = Math.pow(2, zoom);
    const tileXDouble = ((lng + 180) / 360) * numTiles;
    const latRad = (lat * Math.PI) / 180;
    const tileYDouble = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * numTiles;

    const tileX = Math.floor(tileXDouble);
    const tileY = Math.floor(tileYDouble);

    const offsetX = (tileXDouble - tileX) * 256;
    const offsetY = (tileYDouble - tileY) * 256;

    const adjX = offsetX < 128 ? tileX - 1 : tileX + 1;
    const adjY = offsetY < 128 ? tileY - 1 : tileY + 1;

    const xCoords = [Math.min(tileX, adjX), Math.max(tileX, adjX)];
    const yCoords = [Math.min(tileY, adjY), Math.max(tileY, adjY)];

    const tileSpecs = [
      { x: xCoords[0], y: yCoords[0] },
      { x: xCoords[1], y: yCoords[0] },
      { x: xCoords[0], y: yCoords[1] },
      { x: xCoords[1], y: yCoords[1] }
    ];

    let active = true;
    setLoading(true);

    Promise.all(
      tileSpecs.map(spec => {
        return new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = () => reject();
          img.src = `https://a.basemaps.cartocdn.com/light_all/${zoom}/${spec.x}/${spec.y}.png`;
        });
      })
    )
      .then(loadedImgs => {
        if (!active) return;
        setTiles(loadedImgs);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [lat, lng]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    if (!loading && tiles && tiles.length === 4) {
      drawActualMap(ctx, size / 2, size / 2, size, lat, lng, tiles, colors);
    } else if (!loading) {
      drawMiniMap(ctx, size / 2, size / 2, size, spotId, colors);
    } else {
      // Draw background tint while loading
      ctx.save();
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.fillStyle = colors.canvasBg[0];
      ctx.fillRect(0, 0, size, size);
      
      // Center loading indicator
      ctx.fillStyle = 'rgba(252, 251, 247, 0.4)';
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }, [tiles, loading, size, colors, spotId, lat, lng]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: `${size}px`, height: `${size}px` }}
      className="rounded-full shadow-inner border border-white/60 shrink-0"
    />
  );
};

function truncateText(str: string, maxLen: number) {
  if (str.length <= maxLen) return str;
  return str.substring(0, maxLen - 1) + '…';
}

function drawCanvasIcon(ctx: CanvasRenderingContext2D, iconType: string, x: number, y: number, size: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.strokeStyle = 'rgba(252, 251, 247, 0.9)';
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (iconType === 'mountain') {
    ctx.beginPath();
    ctx.moveTo(-size/2, size/2);
    ctx.lineTo(-size/6, -size/6);
    ctx.lineTo(size/12, size/12);
    ctx.lineTo(size/3, -size/4);
    ctx.lineTo(size/2, size/2);
    ctx.closePath();
    ctx.fillStyle = 'rgba(252, 251, 247, 0.2)';
    ctx.fill();
    ctx.stroke();
  } else if (iconType === 'pine') {
    ctx.beginPath();
    ctx.moveTo(0, -size/2);
    ctx.lineTo(-size/2.5, size/4);
    ctx.lineTo(-size/6, size/4);
    ctx.lineTo(-size/6, size/2);
    ctx.lineTo(size/6, size/2);
    ctx.lineTo(size/6, size/4);
    ctx.lineTo(size/2.5, size/4);
    ctx.closePath();
    ctx.fillStyle = 'rgba(252, 251, 247, 0.2)';
    ctx.fill();
    ctx.stroke();
  } else if (iconType === 'star') {
    ctx.beginPath();
    const spikes = 5;
    const outerRadius = size / 2;
    const innerRadius = size / 4;
    let rot = Math.PI / 2 * 3;
    let cx = 0;
    let cy = 0;
    let step = Math.PI / spikes;

    ctx.moveTo(0, -outerRadius);
    for (let i = 0; i < spikes; i++) {
      cx = Math.cos(rot) * outerRadius;
      cy = Math.sin(rot) * outerRadius;
      ctx.lineTo(cx, cy);
      rot += step;

      cx = Math.cos(rot) * innerRadius;
      cy = Math.sin(rot) * innerRadius;
      ctx.lineTo(cx, cy);
      rot += step;
    }
    ctx.closePath();
    ctx.fillStyle = 'rgba(253, 230, 138, 0.85)';
    ctx.fill();
    ctx.strokeStyle = '#fde68a';
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.roundRect(-size/2, -size/4, size, size/2, size/8);
    ctx.fillStyle = 'rgba(252, 251, 247, 0.15)';
    ctx.fill();
    ctx.stroke();
    // Wheels
    ctx.beginPath();
    ctx.arc(-size/4, size/4, size/8, 0, Math.PI * 2);
    ctx.arc(size/4, size/4, size/8, 0, Math.PI * 2);
    ctx.fillStyle = '#1c1917';
    ctx.fill();
  }

  ctx.restore();
}

function drawRetroStickerToCanvas(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  width: number,
  height: number,
  colors: any,
  design: any,
  muniName: string,
  spotName: string,
  latStr: string,
  lngStr: string,
  year: number,
  useMiniMap?: boolean,
  spotId?: string,
  lat?: number,
  lng?: number,
  mapTiles?: HTMLImageElement[]
) {
  ctx.save();
  ctx.translate(cx, cy);

  const grad = ctx.createLinearGradient(-width / 2, -height / 2, width / 2, height / 2);
  grad.addColorStop(0, colors.canvasBg[0]);
  grad.addColorStop(1, colors.canvasBg[1]);

  ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
  ctx.shadowBlur = 15;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 6;

  if (design.shape === 'circle') {
    const r = width / 2;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#fcfbf7';
    ctx.stroke();

    ctx.shadowColor = 'transparent';
    ctx.beginPath();
    ctx.arc(0, 0, r - 8, 0, Math.PI * 2);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(252, 251, 247, 0.25)';
    ctx.stroke();

    ctx.fillStyle = 'rgba(252, 251, 247, 0.7)';
    ctx.font = '900 13px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(muniName.toUpperCase().replace(' KOMMUNE', ''), 0, -r + 20);

    if (useMiniMap && spotId) {
      if (mapTiles && mapTiles.length === 4 && lat !== undefined && lng !== undefined) {
        drawActualMap(ctx, 0, -10, 56, lat, lng, mapTiles, colors);
      } else {
        drawMiniMap(ctx, 0, -10, 56, spotId, colors);
      }
    } else {
      drawCanvasIcon(ctx, design.icon, 0, -10, 38);
    }

    ctx.fillStyle = '#fcfbf7';
    ctx.font = '900 15px sans-serif';
    ctx.fillText(truncateText(spotName, 14), 0, 20);

    ctx.fillStyle = 'rgba(252, 251, 247, 0.6)';
    ctx.font = 'bold 11px monospace';
    ctx.fillText(`${latStr} · EST.${year}`, 0, r - 30);

  } else if (design.shape === 'pill') {
    const rx = 40;
    ctx.beginPath();
    ctx.roundRect(-width / 2, -height / 2, width, height, rx);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#fcfbf7';
    ctx.stroke();

    ctx.shadowColor = 'transparent';
    ctx.beginPath();
    ctx.roundRect(-width / 2 + 8, -height / 2 + 8, width - 16, height - 16, rx - 8);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(252, 251, 247, 0.2)';
    ctx.stroke();

    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    
    ctx.fillStyle = 'rgba(252, 251, 247, 0.7)';
    ctx.font = '900 12px sans-serif';
    ctx.fillText(muniName.toUpperCase().replace(' KOMMUNE', ''), -width / 2 + 25, -height / 2 + 30);

    ctx.fillStyle = '#fcfbf7';
    ctx.font = '900 18px sans-serif';
    ctx.fillText(truncateText(spotName, 12), -width / 2 + 25, 0);

    ctx.fillStyle = 'rgba(252, 251, 247, 0.6)';
    ctx.font = 'bold 11px monospace';
    ctx.fillText(`${latStr} ${lngStr}`, -width / 2 + 25, height / 2 - 30);

    ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
    ctx.beginPath();
    ctx.roundRect(width / 2 - 65, -30, 45, 45, 12);
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(252, 251, 247, 0.1)';
    ctx.stroke();

    if (useMiniMap && spotId) {
      if (mapTiles && mapTiles.length === 4 && lat !== undefined && lng !== undefined) {
        drawActualMap(ctx, width / 2 - 42.5, -7.5, 36, lat, lng, mapTiles, colors);
      } else {
        drawMiniMap(ctx, width / 2 - 42.5, -7.5, 36, spotId, colors);
      }
    } else {
      drawCanvasIcon(ctx, design.icon, width / 2 - 42.5, -7.5, 26);
    }

  } else {
    const rx = 30;
    ctx.beginPath();
    ctx.roundRect(-width / 2, -height / 2, width, height, rx);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#fcfbf7';
    ctx.stroke();

    ctx.shadowColor = 'transparent';
    ctx.beginPath();
    ctx.roundRect(-width / 2 + 8, -height / 2 + 8, width - 16, height - 16, rx - 8);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(252, 251, 247, 0.15)';
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    ctx.fillStyle = 'rgba(252, 251, 247, 0.7)';
    ctx.font = 'bold 11px monospace';
    ctx.fillText(`${latStr} ${lngStr}`, 0, -height / 2 + 20);

    if (useMiniMap && spotId) {
      if (mapTiles && mapTiles.length === 4 && lat !== undefined && lng !== undefined) {
        drawActualMap(ctx, 0, -10, 52, lat, lng, mapTiles, colors);
      } else {
        drawMiniMap(ctx, 0, -10, 52, spotId, colors);
      }
    } else {
      drawCanvasIcon(ctx, design.icon, 0, -10, 36);
    }

    ctx.fillStyle = '#fcfbf7';
    ctx.font = '900 17px sans-serif';
    ctx.fillText(truncateText(spotName, 12), 0, 20);

    ctx.fillStyle = 'rgba(252, 251, 247, 0.6)';
    ctx.font = '900 13px sans-serif';
    ctx.fillText(muniName.toUpperCase().replace(' KOMMUNE', ''), 0, height / 2 - 32);
  }

  ctx.restore();
}

function getCleanMuniForLogo(muni: string): string {
  if (!muni) return '';
  return muni
    .replace(/(?:kommune|herad|fylke)/gi, '')
    .trim()
    .replace(/\s+/g, '_');
}

/**
 * Generic image loader that walks through a list of candidate URLs.
 * On each img error it tries the next URL; when all fail it calls onAllFailed.
 */
const LogoImage: React.FC<{
  urls: string[];
  altText: string;
  onAllFailed: () => void;
  circle?: boolean;
}> = ({ urls, altText, onAllFailed, circle = false }) => {
  const [urlIndex, setUrlIndex] = React.useState(0);

  // Reset when the url list changes (e.g. better municipality data loads in)
  React.useEffect(() => { setUrlIndex(0); }, [urls[0]]);

  const handleError = React.useCallback(() => {
    if (urlIndex + 1 < urls.length) {
      setUrlIndex(idx => idx + 1);
    } else {
      onAllFailed();
    }
  }, [urlIndex, urls.length, onAllFailed]);

  if (!urls.length) return null;

  const currentUrl = urls[urlIndex];
  const isCoatOfArms = currentUrl.includes('wikimedia.org');

  if (isCoatOfArms) {
    return (
      <div className="w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-inner border border-white/60 shrink-0">
        <img
          key={currentUrl}
          src={currentUrl}
          alt={altText}
          draggable={false}
          className="w-5 h-5 object-contain pointer-events-none"
          onError={handleError}
        />
      </div>
    );
  } else {
    return (
      <div className={`w-7 h-7 ${circle ? 'rounded-full' : 'rounded-lg'} overflow-hidden shadow-inner border border-white/30 shrink-0 bg-white`}>
        <img
          key={currentUrl}
          src={currentUrl}
          alt={altText}
          draggable={false}
          className="w-full h-full object-cover pointer-events-none"
          onError={handleError}
        />
      </div>
    );
  }
};


/** Build ordered Wikimedia coat-of-arms candidate URLs for a municipality or county name */
function buildCoatOfArmsUrls(muniName: string): string[] {
  const cleaned = getCleanMuniForLogo(muniName); // e.g. "Vestland", "Faxe"
  const enc = encodeURIComponent(cleaned);
  const base = 'https://commons.wikimedia.org/w/index.php?title=Special:Redirect/file/';
  return [
    `${base}${enc}_komm.svg&width=150`,              // Norwegian municipality: Bergen_komm.svg
    `${base}${enc}_v%C3%A5pen.svg&width=150`,        // Norwegian county:       Vestland_våpen.svg
    `${base}${enc}_kommunev%C3%A5ben.svg&width=150`, // Danish municipality:    Faxe_kommunevåben.svg
    `${base}Coat_of_arms_of_${enc}.svg&width=150`,   // English naming:         Coat_of_arms_of_Bergen.svg
  ];
}

/** Extract domain from a URL string, returns null if unparseable */
function extractDomain(url: string): string | null {
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

function buildCampsiteLogoUrls(spot: { website?: string; photoUrl?: string } | undefined): string[] {
  if (!spot) return [];
  const urls: string[] = [];
  const domain = spot.website ? extractDomain(spot.website) : null;
  if (domain) {
    urls.push(`https://logo.clearbit.com/${domain}`);           // Clearbit: high-res logo
    urls.push(`https://www.google.com/s2/favicons?domain=${domain}&sz=128`); // Google favicon
  }
  if (spot.photoUrl) urls.push(spot.photoUrl);                  // Spot's own photo as last resort
  return urls;
}

/** Build all sticker icon / photo URL candidates in prioritized order */
function buildStickerIconUrls(
  visit: SpotVisit & { useLogo?: boolean },
  spot?: CampingSpot | null
): string[] {
  const coatUrls = spot?.municipality || spot?.property?.kommunenavn || visit.county
    ? buildCoatOfArmsUrls(spot?.municipality || spot?.property?.kommunenavn || visit.county || '')
    : [];

  const photoUrls: string[] = [];
  if (visit.photoUrl) photoUrls.push(visit.photoUrl);
  if (spot?.photoUrls && spot.photoUrls.length > 0) {
    spot.photoUrls.forEach(url => {
      if (url && !photoUrls.includes(url)) photoUrls.push(url);
    });
  }
  if (spot?.photoUrl && !photoUrls.includes(spot.photoUrl)) photoUrls.push(spot.photoUrl);
  if (spot?.ugcThumbnailUrl && !photoUrls.includes(spot.ugcThumbnailUrl)) photoUrls.push(spot.ugcThumbnailUrl);
  if (spot?.auto_image_url && !photoUrls.includes(spot.auto_image_url)) photoUrls.push(spot.auto_image_url);

  const campsiteUrls: string[] = [];
  if (spot?.website) {
    const domain = extractDomain(spot.website);
    if (domain) {
      campsiteUrls.push(`https://logo.clearbit.com/${domain}`);
      campsiteUrls.push(`https://www.google.com/s2/favicons?domain=${domain}&sz=128`);
    }
  }

  const combined = visit.useLogo
    ? [...coatUrls, ...photoUrls, ...campsiteUrls]
    : [...photoUrls, ...campsiteUrls, ...coatUrls];

  return Array.from(new Set(combined.filter(Boolean)));
}

export const StickerBoard: React.FC<StickerBoardProps> = ({ visits, spots, user }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const [layout, setLayout] = useState<Record<string, StickerLayoutItem>>({});
  const [activeSticker, setActiveSticker] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [extraSpots, setExtraSpots] = useState<CampingSpot[]>([]);
  const [resolvedPlaceNames, setResolvedPlaceNames] = useState<Record<string, string>>({});
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    if (!user?.uid) return;
    return subscribeToVehicles(user.uid, setVehicles);
  }, [user?.uid]);

  const plateVehicle = useMemo(() => {
    const hasPlate = (v: Vehicle) => !v.is_retired && v.registration_number && v.registration_number.trim();
    return vehicles.find(v => hasPlate(v) && v.is_default)
        || vehicles.find(v => hasPlate(v))
        || null;
  }, [vehicles]);

  const plateRegnr = plateVehicle?.registration_number?.trim().toUpperCase() || null;
  const plateCountry = getCountryCodeFromLanguage(user.language);
  const userName = user.displayName || user.email?.split('@')[0] || 'Fricamper';
  const userInitials = getUserInitials(user.displayName, user.email);
  // Board zoom: 0.4–1.5, persisted in localStorage
  const [boardZoom, setBoardZoom] = useState<number>(() => {
    try { return parseFloat(localStorage.getItem('fricamp_board_zoom') || '0.7'); } catch { return 0.7; }
  });
  const handleBoardZoom = (delta: number) => {
    setBoardZoom(prev => {
      const next = Math.min(1.5, Math.max(0.4, parseFloat((prev + delta).toFixed(2))));
      try { localStorage.setItem('fricamp_board_zoom', String(next)); } catch {}
      return next;
    });
  };
  
  const boardRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    spotId: string;
    startX: number;
    startY: number;
    itemStartX: number;
    itemStartY: number;
  } | null>(null);
  const fetchedIdsRef = useRef<Set<string>>(new Set());
  const geocodingInProgressRef = useRef<Set<string>>(new Set());
  // Always-fresh layout ref so drag handlers don't capture stale closure
  const layoutRef = useRef<Record<string, StickerLayoutItem>>(layout);
  useEffect(() => { layoutRef.current = layout; }, [layout]);

  // Load visited spots that are not currently passed via the spots prop (e.g. because they are outside map viewport)
  useEffect(() => {
    const missingIds = visits
      .map(v => v.spotId)
      .filter(id => !spots.some(s => s.id === id) && !extraSpots.some(s => s.id === id) && !fetchedIdsRef.current.has(id));

    if (missingIds.length === 0) return;

    missingIds.forEach(id => fetchedIdsRef.current.add(id));

    fetchSpotsByIds(missingIds)
      .then(fetched => {
        setExtraSpots(prev => {
          const map = new Map<string, CampingSpot>();
          prev.forEach(s => map.set(s.id, s));
          fetched.forEach(s => map.set(s.id, s));
          return Array.from(map.values());
        });
      })
      .catch(err => {
        console.error('Failed to fetch extra spots for sticker board:', err);
        // Clear failed fetches from the fetchedIdsRef so they can be retried later
        missingIds.forEach(id => fetchedIdsRef.current.delete(id));
      });
  }, [visits, spots]); // Note: intentionally excludes extraSpots to avoid infinite loop (fetchedIdsRef guards against duplicates)

  const allAvailableSpots = useMemo(() => {
    const map = new Map<string, CampingSpot>();
    spots.forEach(s => map.set(s.id, s));
    extraSpots.forEach(s => map.set(s.id, s));
    return Array.from(map.values());
  }, [spots, extraSpots]);

  // Load layout from user document
  useEffect(() => {
    if (user.stickerLayout) {
      setLayout(user.stickerLayout);
    }
  }, [user.stickerLayout]);

  // Save layout to Firestore
  const handleSaveLayout = async (newLayout: Record<string, StickerLayoutItem>) => {
    if (!user.uid) return;
    setSaveStatus('saving');
    try {
      await updateUserSettings(user.uid, { stickerLayout: newLayout });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Failed to save sticker board layout:', err);
      setSaveStatus('error');
    }
  };

  // Find unique visits to place (a user might have multiple visits to the same spot, we only place one sticker)
  const uniqueVisits = useMemo(() => {
    const seen = new Set<string>();
    return visits.filter(v => {
      if (seen.has(v.spotId)) return false;
      seen.add(v.spotId);
      return true;
    });
  }, [visits]);

  // Sort visits chronologically, mark first visit in each municipality as useLogo (official coat of arms)
  // and subsequent visits as retro badges.
  const visitStickerTypes = useMemo(() => {
    const sorted = [...uniqueVisits].sort((a, b) => a.visitedAt - b.visitedAt);
    
    // Filter to only include visits to fricamp or commercial campsites
    const filtered = sorted.filter(visit => {
      const spot = allAvailableSpots.find(s => s.id === visit.spotId);
      if (!spot) return false; // hide while loading (will show up once fetched if allowed)
      return ['confirmed', 'wildspot', 'algorithmic', 'commercial_campsite'].includes(spot.spotType);
    });

    const seenMunicipalities = new Set<string>();
    
    return filtered.map(visit => {
      const spot = allAvailableSpots.find(s => s.id === visit.spotId);
      const rawMuni = spot?.municipality || spot?.property?.kommunenavn || visit.county || 'Fricamp';
      const cleanMuniKey = rawMuni
        .replace(/(?:kommune|herad|fylke)/gi, '')
        .trim()
        .toLowerCase();
      
      let useLogo = false;
      if (cleanMuniKey && cleanMuniKey !== 'fricamp') {
        if (!seenMunicipalities.has(cleanMuniKey)) {
          seenMunicipalities.add(cleanMuniKey);
          useLogo = true;
        }
      }
      
      return {
        ...visit,
        useLogo,
        municipalityName: spot?.municipality || spot?.property?.kommunenavn || visit.county || 'Fricamp'
      };
    });
  }, [uniqueVisits, allAvailableSpots]);

  // For all fricamp visits, reverse geocode the coordinates to find the local stedsnavn
  useEffect(() => {
    const fricampVisits = visitStickerTypes.filter(visit => {
      const spot = allAvailableSpots.find(s => s.id === visit.spotId);
      const isFricamp = spot && ['confirmed', 'wildspot', 'algorithmic'].includes(spot.spotType);
      return isFricamp && !resolvedPlaceNames[visit.spotId] && !geocodingInProgressRef.current.has(visit.spotId);
    });

    if (fricampVisits.length === 0) return;

    // Mark as in progress
    fricampVisits.forEach(visit => geocodingInProgressRef.current.add(visit.spotId));

    let active = true;
    const resolveNames = async () => {
      for (const visit of fricampVisits) {
        if (!active) {
          geocodingInProgressRef.current.delete(visit.spotId);
          continue;
        }
        try {
          await new Promise(r => setTimeout(r, 250)); // rate limiting throttle
          if (!active) {
            geocodingInProgressRef.current.delete(visit.spotId);
            continue;
          }
          const name = await reverseGeocode({ latitude: visit.latitude, longitude: visit.longitude });
          if (!active) {
            geocodingInProgressRef.current.delete(visit.spotId);
            continue;
          }

          if (name) {
            const isCoord = /^\d+\.\d+°[NSEW]/.test(name);
            if (!isCoord) {
              const cleanName = name.split(',')[0].trim();
              setResolvedPlaceNames(prev => ({
                ...prev,
                [visit.spotId]: cleanName
              }));
            }
          }
        } catch (err) {
          console.warn(`Failed to reverse geocode for spot ${visit.spotId}:`, err);
          geocodingInProgressRef.current.delete(visit.spotId);
        }
      }
    };

    resolveNames();

    return () => {
      active = false;
      // Also release any spots that were marked in progress but not yet processed
      fricampVisits.forEach(visit => {
        if (!resolvedPlaceNames[visit.spotId]) {
          geocodingInProgressRef.current.delete(visit.spotId);
        }
      });
    };
  }, [visitStickerTypes, allAvailableSpots]);

  // Categorize visits into active (on window) and unused (in drawer)
  const activeStickers = useMemo(() => visitStickerTypes.filter(v => layout[v.spotId] !== undefined), [visitStickerTypes, layout]);
  const unusedStickers = useMemo(() => visitStickerTypes.filter(v => layout[v.spotId] === undefined), [visitStickerTypes, layout]);

  // Add sticker to window (center of the window)
  const handleAddSticker = (spotId: string) => {
    const newLayout = {
      ...layout,
      [spotId]: {
        x: 40 + Math.random() * 20,
        y: 40 + Math.random() * 20,
        rotation: (Math.random() * 20) - 10,
        scale: 0.8, // default small so more stickers fit
      }
    };
    setLayout(newLayout);
    setActiveSticker(spotId);
    handleSaveLayout(newLayout);
  };

  const handleSendEmail = async () => {
    if (!user.uid || !user.email) return;
    setEmailStatus('sending');
    
    try {
      // 0. Pre-load user avatar for PDF header
      let userAvatarImg: HTMLImageElement | null = null;
      if (user.photoURL) {
        await new Promise<void>((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => { userAvatarImg = img; resolve(); };
          img.onerror = () => resolve();
          img.src = user.photoURL!;
        });
      }

      // 1. Prepare and pre-load all images / map tiles for canvas drawing
      const activeStickersToDraw = visitStickerTypes.filter(v => layout[v.spotId] !== undefined);
      const loadedImages: Record<string, HTMLImageElement> = {};
      const loadedMapTiles: Record<string, HTMLImageElement[]> = {};
      
      await Promise.all(activeStickersToDraw.map(visit => {
        const spot = allAvailableSpots.find(s => s.id === visit.spotId);
        const isFricamp = spot && ['confirmed', 'wildspot', 'algorithmic'].includes(spot.spotType);

        // 1. Load map tiles if it is a fricamp (as fallback)
        let mapPromise = Promise.resolve();
        if (isFricamp) {
          const lat = visit.latitude;
          const lng = visit.longitude;
          const zoom = 14;
          const numTiles = Math.pow(2, zoom);
          const tileXDouble = ((lng + 180) / 360) * numTiles;
          const latRad = (lat * Math.PI) / 180;
          const tileYDouble = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * numTiles;

          const tileX = Math.floor(tileXDouble);
          const tileY = Math.floor(tileYDouble);

          const offsetX = (tileXDouble - tileX) * 256;
          const offsetY = (tileYDouble - tileY) * 256;

          const adjX = offsetX < 128 ? tileX - 1 : tileX + 1;
          const adjY = offsetY < 128 ? tileY - 1 : tileY + 1;

          const xCoords = [Math.min(tileX, adjX), Math.max(tileX, adjX)];
          const yCoords = [Math.min(tileY, adjY), Math.max(tileY, adjY)];

          const tileSpecs = [
            { x: xCoords[0], y: yCoords[0] },
            { x: xCoords[1], y: yCoords[0] },
            { x: xCoords[0], y: yCoords[1] },
            { x: xCoords[1], y: yCoords[1] }
          ];

          mapPromise = Promise.all(
            tileSpecs.map(spec => {
              return new Promise<HTMLImageElement>((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => resolve(img);
                img.onerror = () => reject();
                img.src = `https://a.basemaps.cartocdn.com/light_all/${zoom}/${spec.x}/${spec.y}.png`;
              });
            })
          )
            .then(tiles => {
              loadedMapTiles[visit.spotId] = tiles;
            })
            .catch(err => {
              console.error(`Failed to preload map tiles for spot ${visit.spotId}:`, err);
            });
        }

        // 2. Load icon/photo candidates
        const iconUrls = buildStickerIconUrls(visit, spot || null);
        let iconPromise = Promise.resolve();
        if (iconUrls.length > 0) {
          iconPromise = new Promise<void>((resolve) => {
            let index = 0;
            const tryNext = () => {
              if (index >= iconUrls.length) {
                resolve();
                return;
              }
              const img = new Image();
              img.crossOrigin = 'anonymous'; // CRITICAL FOR CANVAS CORS
              img.onload = () => {
                loadedImages[visit.spotId] = img;
                resolve();
              };
              img.onerror = () => {
                index++;
                tryNext();
              };
              img.src = iconUrls[index];
            };
            tryNext();
          });
        }

        return Promise.all([mapPromise, iconPromise]);
      }));

      // 2. Create high-resolution canvas (A-paper landscape ratio: 2400 / √2 ≈ 1697)
      const canvas = document.createElement('canvas');
      canvas.width = 2400;
      canvas.height = 1697;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get 2D context');

      // 3. Draw Background (radial gradient Zinc-800 to Stone-950)
      const bgGrad = ctx.createRadialGradient(1200, 250, 100, 1200, 848, 1500);
      bgGrad.addColorStop(0, '#27272a'); // zinc-800
      bgGrad.addColorStop(0.5, '#1c1917'); // stone-900
      bgGrad.addColorStop(1, '#0c0a09'); // stone-950
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 4. Draw rear window wires (heated glass grid)
      ctx.strokeStyle = 'rgba(234, 88, 12, 0.08)';
      ctx.lineWidth = 4;
      const padding = 150;
      const gridHeight = canvas.height - (padding * 2);
      for (let i = 0; i < 6; i++) {
        const y = padding + (gridHeight / 5) * i;
        ctx.beginPath();
        ctx.moveTo(50, y);
        ctx.lineTo(2350, y);
        ctx.stroke();
      }

      // 6. Draw stickers
      activeStickersToDraw.forEach(visit => {
        const pos = layout[visit.spotId] || { x: 50, y: 50, rotation: 0, scale: 1.0 };
        const cx = (pos.x / 100) * canvas.width;
        const cy = (pos.y / 100) * canvas.height;
        const scale = pos.scale || 1.0;
        const rotationRad = (pos.rotation * Math.PI) / 180;

        const spot = allAvailableSpots.find(s => s.id === visit.spotId);
        const muni = spot?.municipality || spot?.property?.kommunenavn;
        const img = loadedImages[visit.spotId]; // coat of arms image if loaded

        const isFricamp = spot && ['confirmed', 'wildspot', 'algorithmic'].includes(spot.spotType);
        const spotName = (isFricamp && resolvedPlaceNames[visit.spotId])
          ? resolvedPlaceNames[visit.spotId]
          : (visit.spotName || t('stickerBoard.default_spot_name', 'Naturplass'));
        const latStr = `${visit.latitude.toFixed(3)}°N`;
        const lngStr = `${visit.longitude.toFixed(3)}°E`;
        const year = new Date(visit.visitedAt).getFullYear();
        const colors = getRetroColors(visit.spotId);
        const design = getRetroShape(visit.spotId);

        let drawWidth: number;
        let drawHeight: number;
        if (design.shape === 'pill') {
          drawWidth = 300 * scale;
          drawHeight = 155 * scale;
        } else if (design.shape === 'circle') {
          drawWidth = 220 * scale;
          drawHeight = 220 * scale;
        } else {
          drawWidth = 190 * scale;
          drawHeight = 190 * scale;
        }

        // Draw the retro badge shape with the coat of arms embedded as the icon
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(rotationRad);

        const useMiniMap = isFricamp && !img;

        // Draw the base retro badge
        drawRetroStickerToCanvas(
          ctx, 0, 0,
          drawWidth, drawHeight,
          colors, design,
          visit.municipalityName || 'Fricamp',
          spotName, latStr, lngStr, year,
          useMiniMap || false,
          visit.spotId,
          visit.latitude,
          visit.longitude,
          loadedMapTiles[visit.spotId]
        );

        // If we have loaded a fallback/primary image, draw it over the icon area
        if (img) {
          const logoRadius = drawWidth * 0.14; // white circle / backing size
          let logoX = 0;
          let logoY = -drawHeight * 0.05; // roughly icon area center

          if (design.shape === 'pill') {
            logoX = -drawWidth / 2 + logoRadius + 10;
            logoY = 0;
          }

          ctx.shadowColor = 'transparent';

          const isCoatOfArms = img.src.includes('wikimedia.org');

          if (isCoatOfArms) {
            // White backing circle
            ctx.beginPath();
            ctx.arc(logoX, logoY, logoRadius, 0, Math.PI * 2);
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.lineWidth = 2;
            ctx.strokeStyle = 'rgba(252, 251, 247, 0.6)';
            ctx.stroke();

            // Draw the coat of arms clipped to the circle
            ctx.save();
            ctx.beginPath();
            ctx.arc(logoX, logoY, logoRadius * 0.85, 0, Math.PI * 2);
            ctx.clip();
            const imgSize = logoRadius * 1.7;
            ctx.drawImage(img, logoX - imgSize / 2, logoY - imgSize / 2, imgSize, imgSize);
            ctx.restore();
          } else {
            // Campsite logo/photo — rounded square or circle depending on sticker shape
            const rectSize = logoRadius * 2;
            const rx = logoRadius * 0.4;
            
            ctx.save();
            if (design.shape === 'circle') {
              // Draw backing circle for circular stickers
              ctx.beginPath();
              ctx.arc(logoX, logoY, logoRadius, 0, Math.PI * 2);
              ctx.fillStyle = '#ffffff';
              ctx.fill();
              ctx.lineWidth = 2;
              ctx.strokeStyle = 'rgba(252, 251, 247, 0.4)';
              ctx.stroke();

              // Clip and draw photo in circle
              ctx.beginPath();
              ctx.arc(logoX, logoY, logoRadius * 0.9, 0, Math.PI * 2);
              ctx.clip();
              ctx.drawImage(img, logoX - logoRadius * 0.9, logoY - logoRadius * 0.9, logoRadius * 1.8, logoRadius * 1.8);
            } else {
              // Draw backing rounded rect for pill/square stickers
              ctx.beginPath();
              ctx.roundRect(logoX - rectSize / 2, logoY - rectSize / 2, rectSize, rectSize, rx);
              ctx.fillStyle = '#ffffff';
              ctx.fill();
              ctx.lineWidth = 2;
              ctx.strokeStyle = 'rgba(252, 251, 247, 0.4)';
              ctx.stroke();

              // Clip and draw
              ctx.beginPath();
              ctx.roundRect(logoX - rectSize * 0.9 / 2, logoY - rectSize * 0.9 / 2, rectSize * 0.9, rectSize * 0.9, rx * 0.9);
              ctx.clip();
              ctx.drawImage(img, logoX - rectSize * 0.9 / 2, logoY - rectSize * 0.9 / 2, rectSize * 0.9, rectSize * 0.9);
            }
            ctx.restore();
          }
        }

        ctx.restore();
      });


      // 7. Draw user-tag pill at top center
      {
        const pdfUserName = (user.displayName || user.email || t('stickerBoard.default_user', 'Fricamp-reisende')).toUpperCase();
        const avSize = 72;
        const gap = 20;
        const padX = 28;
        const padY = 14;
        const nameFontSize = 36;
        ctx.font = `900 ${nameFontSize}px "Arial Black", Helvetica, sans-serif`;
        const nameWidth = ctx.measureText(pdfUserName).width;
        const pillW = padX + avSize + gap + nameWidth + padX;
        const pillH = avSize + padY * 2;
        const pillX = 1200 - pillW / 2;
        const pillY = 60;

        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 18;
        ctx.shadowOffsetY = 6;
        ctx.fillStyle = 'rgba(28, 25, 23, 0.88)';
        ctx.beginPath();
        ctx.roundRect(pillX, pillY, pillW, pillH, pillH / 2);
        ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(120, 113, 108, 0.6)';
        ctx.stroke();
        ctx.restore();

        const avCx = pillX + padX + avSize / 2;
        const avCy = pillY + pillH / 2;
        if (userAvatarImg) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(avCx, avCy, avSize / 2, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(userAvatarImg, avCx - avSize / 2, avCy - avSize / 2, avSize, avSize);
          ctx.restore();
          ctx.beginPath();
          ctx.arc(avCx, avCy, avSize / 2, 0, Math.PI * 2);
          ctx.lineWidth = 2;
          ctx.strokeStyle = 'rgba(120, 113, 108, 0.7)';
          ctx.stroke();
        } else {
          ctx.fillStyle = '#57534e';
          ctx.beginPath();
          ctx.arc(avCx, avCy, avSize / 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#e7e5e4';
          ctx.font = '900 28px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(userInitials, avCx, avCy);
        }

        ctx.fillStyle = '#e7e5e4';
        ctx.font = `900 ${nameFontSize}px "Arial Black", Helvetica, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(pdfUserName, avCx + avSize / 2 + gap, avCy);
      }

      // Small Fricamp branding bottom-right
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillStyle = 'rgba(252, 251, 247, 0.2)';
      ctx.font = '700 22px sans-serif';
      ctx.fillText('fricamp.com', 2350, canvas.height - 30);

      // 7b. Draw EU license plate mounted at the bottom edge of the rear window
      {
        const plateH = 134;
        const plateW = plateRegnr ? 672 : 768;
        const plateBottom = canvas.height;
        const plateTop = plateBottom - plateH;
        const plateLeft = 1200 - plateW / 2;
        const stripW = 96;

        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
        ctx.shadowBlur = 22;
        ctx.shadowOffsetY = -8;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.roundRect(plateLeft, plateTop, plateW, plateH, [14, 14, 0, 0]);
        ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.lineWidth = 5;
        ctx.strokeStyle = '#1c1917';
        ctx.beginPath();
        ctx.moveTo(plateLeft, plateBottom);
        ctx.lineTo(plateLeft, plateTop + 14);
        ctx.quadraticCurveTo(plateLeft, plateTop, plateLeft + 14, plateTop);
        ctx.lineTo(plateLeft + plateW - 14, plateTop);
        ctx.quadraticCurveTo(plateLeft + plateW, plateTop, plateLeft + plateW, plateTop + 14);
        ctx.lineTo(plateLeft + plateW, plateBottom);
        ctx.stroke();
        ctx.restore();

        ctx.save();
        ctx.beginPath();
        ctx.roundRect(plateLeft, plateTop, plateW, plateH, [14, 14, 0, 0]);
        ctx.clip();

        ctx.fillStyle = '#003399';
        ctx.fillRect(plateLeft, plateTop, stripW, plateH);

        const stripCx = plateLeft + stripW / 2;
        const stripStarsCy = plateTop + plateH / 2 - 18;
        const ringR = 22;
        ctx.fillStyle = '#FFCC00';
        for (let i = 0; i < 12; i++) {
          const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
          ctx.beginPath();
          ctx.arc(stripCx + ringR * Math.cos(a), stripStarsCy + ringR * Math.sin(a), 2.8, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.fillStyle = '#ffffff';
        ctx.font = '900 30px "Arial Black", Helvetica, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(plateCountry, stripCx, plateTop + plateH - 30);
        ctx.restore();

        const rightLeft = plateLeft + stripW;
        const rightW = plateW - stripW;
        const rightCx = rightLeft + rightW / 2;
        const rightCy = plateTop + plateH / 2;

        if (plateRegnr) {
          ctx.fillStyle = '#000000';
          ctx.font = '900 68px "Arial Black", Helvetica, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const charSpacing = 8;
          const chars = plateRegnr.split('');
          let totalWidth = 0;
          chars.forEach((c, i) => {
            totalWidth += ctx.measureText(c).width;
            if (i < chars.length - 1) totalWidth += charSpacing;
          });
          let cursorX = rightCx - totalWidth / 2;
          chars.forEach(c => {
            const w = ctx.measureText(c).width;
            ctx.fillText(c, cursorX + w / 2, rightCy);
            cursorX += w + charSpacing;
          });
        } else {
          const avSize = 84;
          const avX = rightLeft + 36 + avSize / 2;
          const avY = rightCy;
          if (userAvatarImg) {
            ctx.save();
            ctx.beginPath();
            ctx.arc(avX, avY, avSize / 2, 0, Math.PI * 2);
            ctx.clip();
            ctx.drawImage(userAvatarImg, avX - avSize / 2, avY - avSize / 2, avSize, avSize);
            ctx.restore();
            ctx.beginPath();
            ctx.arc(avX, avY, avSize / 2 + 1, 0, Math.PI * 2);
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#a8a29e';
            ctx.stroke();
          } else {
            ctx.fillStyle = '#e7e5e4';
            ctx.beginPath();
            ctx.arc(avX, avY, avSize / 2, 0, Math.PI * 2);
            ctx.fill();
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#a8a29e';
            ctx.stroke();
            ctx.fillStyle = '#44403c';
            ctx.font = '900 28px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(userInitials, avX, avY);
          }
          ctx.fillStyle = '#000000';
          ctx.font = '900 34px "Arial Black", Helvetica, sans-serif';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(userName.toUpperCase(), avX + avSize / 2 + 22, rightCy);
        }
      }

      // 8. Convert to JPEG data URL
      const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.75);

      // 9. Generate PDF using jsPDF (A3 landscape format: 420mm x 297mm)
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a3'
      });

      // Canvas now matches A-paper landscape ratio (√2) — fill the whole A3 page edge-to-edge
      pdf.addImage(jpegDataUrl, 'JPEG', 0, 0, 420, 297);

      const pdfBase64 = pdf.output('datauristring').split(',')[1];

      // 10. Send PDF via Cloud Function
      await sendStickerBoardEmail(pdfBase64);

      setEmailStatus('success');
      addToast(t('stickerBoard.email_success', 'Reisetavlen er sendt til din e-post som PDF!'), 'success');
      setTimeout(() => setEmailStatus('idle'), 4000);
    } catch (err: any) {
      console.error('Failed to send sticker board email:', err);
      setEmailStatus('error');
      addToast(t('stickerBoard.email_error', 'Kunne ikke sende e-post. Vennligst prøv igjen.'), 'error');
      setTimeout(() => setEmailStatus('idle'), 5000);
    }
  };

  // Remove sticker from window back to drawer
  const handleRemoveSticker = (spotId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const newLayout = { ...layout };
    delete newLayout[spotId];
    setLayout(newLayout);
    if (activeSticker === spotId) {
      setActiveSticker(null);
    }
    handleSaveLayout(newLayout);
  };

  // Rotate selected sticker
  const handleRotateSticker = (spotId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const current = layout[spotId];
    if (!current) return;
    const newLayout = {
      ...layout,
      [spotId]: {
        ...current,
        rotation: (current.rotation + 15) % 360,
      }
    };
    setLayout(newLayout);
    handleSaveLayout(newLayout);
  };

  // Scale selected sticker
  const handleScaleSticker = (spotId: string, factor: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const current = layout[spotId];
    if (!current) return;
    const newScale = Math.min(1.5, Math.max(0.6, current.scale + factor));
    const newLayout = {
      ...layout,
      [spotId]: {
        ...current,
        scale: Number(newScale.toFixed(2)),
      }
    };
    setLayout(newLayout);
    handleSaveLayout(newLayout);
  };

  // Clear all stickers from window
  const handleResetBoard = () => {
    if (window.confirm(t('stickerBoard.confirm_reset', 'Vil du fjerne alle klistremerker fra bakruten?'))) {
      setLayout({});
      setActiveSticker(null);
      handleSaveLayout({});
    }
  };

  // Drag Handlers
  const handleMouseDown = (spotId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveSticker(spotId);
    
    const item = layout[spotId];
    if (!item || !boardRef.current) return;

    dragRef.current = {
      spotId,
      startX: e.clientX,
      startY: e.clientY,
      itemStartX: item.x,
      itemStartY: item.y,
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragRef.current || !boardRef.current) return;
    const { spotId, startX, startY, itemStartX, itemStartY } = dragRef.current;
    
    const rect = boardRef.current.getBoundingClientRect();
    const deltaX = ((e.clientX - startX) / rect.width) * 100;
    const deltaY = ((e.clientY - startY) / rect.height) * 100;

    // Constrain inside window boundary (keep center of sticker inside the glass)
    const newX = Math.min(95, Math.max(5, itemStartX + deltaX));
    const newY = Math.min(92, Math.max(5, itemStartY + deltaY));

    setLayout(prev => ({
      ...prev,
      [spotId]: {
        ...prev[spotId]!,
        x: Number(newX.toFixed(1)),
        y: Number(newY.toFixed(1)),
      }
    }));
  };

  const handleMouseUp = () => {
    if (dragRef.current) {
      handleSaveLayout(layoutRef.current); // use ref to get latest layout, avoiding stale closure
    }
    dragRef.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Touch support for Mobile devices
  const handleTouchStart = (spotId: string, e: React.TouchEvent) => {
    e.stopPropagation();
    setActiveSticker(spotId);
    
    const touch = e.touches[0];
    const item = layout[spotId];
    if (!item || !boardRef.current) return;

    dragRef.current = {
      spotId,
      startX: touch.clientX,
      startY: touch.clientY,
      itemStartX: item.x,
      itemStartY: item.y,
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!dragRef.current || !boardRef.current) return;
    e.preventDefault(); // Prevent scrolling while dragging

    const touch = e.touches[0];
    const { spotId, startX, startY, itemStartX, itemStartY } = dragRef.current;
    
    const rect = boardRef.current.getBoundingClientRect();
    const deltaX = ((touch.clientX - startX) / rect.width) * 100;
    const deltaY = ((touch.clientY - startY) / rect.height) * 100;

    const newX = Math.min(95, Math.max(5, itemStartX + deltaX));
    const newY = Math.min(92, Math.max(5, itemStartY + deltaY));

    setLayout(prev => ({
      ...prev,
      [spotId]: {
        ...prev[spotId]!,
        x: Number(newX.toFixed(1)),
        y: Number(newY.toFixed(1)),
      }
    }));
  };

  const handleTouchEnd = () => {
    if (dragRef.current) {
      handleSaveLayout(layoutRef.current); // use ref to get latest layout
    }
    dragRef.current = null;
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
  };

  // Render a beautiful retro badge inside React
  // muniName: coat-of-arms name (tries 4 URL patterns via LogoImage)
  // spot: full spot data for campsite logo fallback chain
  const renderRetroSticker = (
    visit: SpotVisit & { useLogo?: boolean; municipalityName?: string },
    muniName?: string | null,
    spot?: { website?: string; photoUrl?: string; spotType?: string } | null
  ) => {
    const fullSpot = allAvailableSpots.find(s => s.id === visit.spotId);
    const muni = fullSpot?.municipality || fullSpot?.property?.kommunenavn || visit.county || 'Fricamp';
    const cleanMuni = muni
      .replace(/(?:kommune|herad|fylke)/gi, '')
      .trim()
      .toUpperCase();
    const county = fullSpot?.property?.kommunenavn || visit.county || '';
    const countyClean = county.replace(/(?:kommune|herad|fylke)/gi, '').trim();
    const latStr = `${visit.latitude.toFixed(2)}°N`;
    const lngStr = `${visit.longitude.toFixed(2)}°E`;
    const year = new Date(visit.visitedAt).getFullYear();
    // Compact check-in date: DD.MM.YY
    const visitDate = new Date(visit.visitedAt);
    const dateStr = visitDate.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: '2-digit' });

    const colors = getRetroColors(visit.spotId);
    const design = getRetroShape(visit.spotId);

    const isFricamp = fullSpot && ['confirmed', 'wildspot', 'algorithmic'].includes(fullSpot.spotType);
    const spotName = (isFricamp && resolvedPlaceNames[visit.spotId])
      ? resolvedPlaceNames[visit.spotId]
      : (visit.spotName || t('stickerBoard.default_spot_name', 'Naturplass'));

    // Build icon/photo candidates
    const iconUrls = !imageErrors[visit.spotId]
      ? buildStickerIconUrls(visit, fullSpot || spot || null)
      : [];

    const showIcon = iconUrls.length > 0;
    const useMiniMap = isFricamp && !showIcon;

    // The icon area
    const renderIcon = (iconSize: string = 'w-5 h-5') => {
      if (showIcon) {
        return (
          <LogoImage
            urls={iconUrls}
            altText={spotName}
            onAllFailed={() => setImageErrors(prev => ({ ...prev, [visit.spotId]: true }))}
            circle={design.shape === 'circle'}
          />
        );
      }
      switch (design.icon) {
        case 'mountain':
          return (
            <svg viewBox="0 0 24 24" className={`${iconSize} text-[#fcfbf7]/90 fill-[#fcfbf7]/30`} stroke="currentColor" strokeWidth="2">
              <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
            </svg>
          );
        case 'pine':
          return (
            <svg viewBox="0 0 24 24" className={`${iconSize} text-[#fcfbf7]/90 fill-none`} stroke="currentColor" strokeWidth="2">
              <path d="M12 2L3 15h6v7h6v-7h6z" />
            </svg>
          );
        case 'star':
          return (
            <svg viewBox="0 0 24 24" className={`${iconSize} text-amber-300 fill-amber-300/80`} stroke="currentColor" strokeWidth="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          );
        default:
          return (
            <svg viewBox="0 0 24 24" className={`${iconSize} text-[#fcfbf7]/90 fill-[#fcfbf7]/20`} stroke="currentColor" strokeWidth="2">
              <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1 .4-1 1v10h1M14 17h1" />
              <circle cx="7.5" cy="17.5" r="2.5" className="fill-stone-800" />
              <circle cx="16.5" cy="17.5" r="2.5" className="fill-stone-800" />
            </svg>
          );
      }
    };

    // Circular Badge Shape
    if (design.shape === 'circle') {
      return (
        <div className={`w-24 h-24 rounded-full bg-gradient-to-b ${colors.bg} p-1 text-center flex flex-col items-center justify-between shadow-lg relative border-2 border-[#fcfbf7]`}>
          <div className="absolute inset-1.5 border border-[#fcfbf7]/20 rounded-full pointer-events-none" />
          <span className="text-[6px] font-black text-[#fcfbf7]/75 tracking-widest mt-1.5 uppercase leading-none">{cleanMuni}</span>
          <div className="flex flex-col items-center justify-center flex-1 py-0.5 gap-0.5">
            {useMiniMap ? (
              <ActualMiniMap lat={visit.latitude} lng={visit.longitude} spotId={visit.spotId} colors={colors} size={36} />
            ) : (
              renderIcon('w-6 h-6')
            )}
            <span className="text-[7px] text-[#fcfbf7] font-black tracking-wider leading-none text-center max-w-[75px] truncate">{spotName}</span>
            {countyClean && countyClean.toLowerCase() !== cleanMuni.toLowerCase() && (
              <span className="text-[5.5px] text-[#fcfbf7]/50 font-bold uppercase tracking-wide leading-none">{countyClean}</span>
            )}
          </div>
          <span className="text-[5px] font-mono text-[#fcfbf7]/55 mb-1.5 leading-none">{dateStr} · {latStr}</span>
        </div>
      );
    }

    // Pill Shape — wider to fit logo + text
    if (design.shape === 'pill') {
      return (
        <div className={`w-32 h-16 rounded-card bg-gradient-to-r ${colors.bg} px-2 py-1.5 flex items-center gap-2 shadow-lg relative border-2 border-[#fcfbf7]`}>
          <div className="absolute inset-1 border border-[#fcfbf7]/20 rounded-[1.25rem] pointer-events-none" />
          {/* Icon / logo on the left */}
          <div className="shrink-0">
            {useMiniMap ? (
              <ActualMiniMap lat={visit.latitude} lng={visit.longitude} spotId={visit.spotId} colors={colors} size={28} />
            ) : (
              renderIcon('w-5 h-5')
            )}
          </div>
          {/* Text stack */}
          <div className="flex-1 flex flex-col justify-between h-full py-0 min-w-0">
            <span className="text-[6px] font-black text-[#fcfbf7]/75 tracking-wider truncate uppercase leading-none">{cleanMuni}</span>
            <span className="text-4xs text-[#fcfbf7] font-black leading-none truncate">{spotName}</span>
            <span className="text-[5px] font-mono text-[#fcfbf7]/55 leading-none truncate">{dateStr} · {latStr}</span>
          </div>
        </div>
      );
    }

    // Square / hexagon badge
    return (
      <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${colors.bg} p-1.5 text-center flex flex-col items-center justify-between shadow-lg relative border-2 border-[#fcfbf7]`}>
        <div className="absolute inset-1 border border-[#fcfbf7]/15 rounded-xl pointer-events-none" />
        <span className="text-[6px] font-black text-[#fcfbf7]/75 tracking-widest uppercase leading-none mt-0.5">{cleanMuni}</span>
        <div className="flex flex-col items-center gap-0.5">
          {useMiniMap ? (
            <ActualMiniMap lat={visit.latitude} lng={visit.longitude} spotId={visit.spotId} colors={colors} size={30} />
          ) : (
            renderIcon('w-5 h-5')
          )}
          <span className="text-[7px] text-[#fcfbf7] font-black tracking-wide leading-tight truncate max-w-[65px]">{spotName}</span>
        </div>
        <div className="flex flex-col items-center">
          {countyClean && countyClean.toLowerCase() !== cleanMuni.toLowerCase() && (
            <span className="text-[5px] text-[#fcfbf7]/45 font-bold uppercase tracking-wide leading-none">{countyClean}</span>
          )}
          <span className="text-[5px] font-mono text-[#fcfbf7]/55 leading-none">{dateStr}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full min-h-0 select-none gap-3">
      {/* Toolbar: title, zoom, save status, actions */}
      <div className="flex items-center justify-between px-1 pr-12 md:pr-14 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <Sparkles size={14} className="text-emerald-500 animate-pulse" />
          <h3 className="text-sm font-black text-[var(--text-primary)]">{t('stickerBoard.title', 'Bobilens Bakrute')}</h3>
        </div>
        <div className="flex items-center gap-2">
          {/* Zoom controls */}
          <div className="flex items-center gap-1 bg-stone-900 border border-stone-700 rounded-xl px-1.5 py-1">
            <button
              onClick={() => handleBoardZoom(-0.1)}
              disabled={boardZoom <= 0.4}
              className="w-5 h-5 flex items-center justify-center text-[12px] font-black text-stone-300 hover:text-white disabled:opacity-30 transition-colors"
              title={t('stickerBoard.zoom_out', 'Zoom ut')}
            >−</button>
            <span className="text-3xs font-black text-stone-400 w-7 text-center tabular-nums">{Math.round(boardZoom * 100)}%</span>
            <button
              onClick={() => handleBoardZoom(0.1)}
              disabled={boardZoom >= 1.5}
              className="w-5 h-5 flex items-center justify-center text-[12px] font-black text-stone-300 hover:text-white disabled:opacity-30 transition-colors"
              title={t('stickerBoard.zoom_in', 'Zoom inn')}
            >+</button>
          </div>
          {saveStatus === 'saving' && (
            <span className="text-3xs font-bold uppercase tracking-wider text-[var(--text-muted)] flex items-center gap-1 animate-pulse">
              <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-ping" /> {t('stickerBoard.saving', 'Lagrer...')}
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-3xs font-black uppercase tracking-wider text-emerald-400 flex items-center gap-1">
              ✓ {t('stickerBoard.saved', 'Lagret')}
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="text-3xs font-black uppercase tracking-wider text-red-400 flex items-center gap-1">
              ⚠ {t('stickerBoard.save_error', 'Feil under lagring')}
            </span>
          )}
          <button
            onClick={handleSendEmail}
            disabled={activeStickers.length === 0 || emailStatus === 'sending'}
            className="px-2.5 py-1.5 text-3xs font-black uppercase tracking-widest bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 rounded-xl transition-all border border-emerald-500/20 active:scale-95 disabled:opacity-40 disabled:pointer-events-none flex items-center gap-1.5"
          >
            {emailStatus === 'sending' ? (
              <>
                <Loader2 size={10} className="animate-spin text-emerald-400" />
                {t('stickerBoard.sending_pdf', 'Sender PDF...')}
              </>
            ) : emailStatus === 'success' ? (
              <>
                <CheckCircle2 size={10} className="text-emerald-400" />
                {t('stickerBoard.sent', 'Sendt!')}
              </>
            ) : (
              <>
                <Mail size={10} className="text-emerald-400" />
                {t('stickerBoard.send_email', 'Send e-post (PDF)')}
              </>
            )}
          </button>
          <button
            onClick={handleResetBoard}
            disabled={activeStickers.length === 0}
            className="px-2.5 py-1.5 text-3xs font-black uppercase tracking-widest bg-red-600/10 hover:bg-red-600/20 text-red-400 rounded-xl transition-all border border-red-500/20 active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('stickerBoard.reset_board', 'Tøm rute')}
          </button>
        </div>
      </div>

      {/* Main glass window container */}
      <div className="relative w-full rounded-2xl md:rounded-panel bg-stone-900/90 border-[4px] md:border-[6px] border-stone-800 shadow-2xl p-2.5 md:p-4 overflow-hidden flex flex-col items-center flex-1 min-h-0">
        {/* Background wood panel/corkboard container effect */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-neutral-800 via-neutral-900 to-black pointer-events-none opacity-40" />

        {/* Scrollable board wrapper */}
        <div className="w-full overflow-auto flex-1 min-h-0 p-1 md:p-2 custom-scrollbar">
          <div
            style={{
              width: `${1000 * boardZoom}px`,
              height: `${707 * boardZoom}px`,
              transition: 'width 0.15s ease-out, height 0.15s ease-out',
              position: 'relative',
            }}
            className="flex-shrink-0 mx-auto"
          >
            {/* Virtuell Bakrute (Van Window) */}
            <div
              ref={boardRef}
              onClick={() => setActiveSticker(null)}
              style={{
                width: '1000px',
                height: '707px',
                transform: `scale(${boardZoom})`,
                transformOrigin: 'top left',
                transition: 'transform 0.15s ease-out',
                position: 'absolute',
                left: 0,
                top: 0,
              }}
              className="bg-gradient-to-br from-zinc-800 to-stone-950 rounded-2xl border-4 border-zinc-900 shadow-inner overflow-hidden"
            >
              {/* Glass Gloss/Reflex Overlay */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 pointer-events-none z-10" />
              <div className="absolute -top-1/2 -left-1/4 w-full h-[150%] bg-white/3 rotate-12 pointer-events-none transform origin-top-left z-10" />
              
              {/* Heated glass grid lines (Nostalgic rear window wires) */}
              <div className="absolute inset-0 flex flex-col justify-between py-8 opacity-5 pointer-events-none">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="w-full h-0.5 bg-orange-600" />
                ))}
              </div>


              {/* Empty board guide */}
              {activeStickers.length === 0 && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 pointer-events-none">
                  <Compass size={36} className="text-zinc-600 animate-spin-slow mb-2.5" />
                  <p className="text-tiny font-black text-zinc-500 uppercase tracking-widest">{t('stickerBoard.empty_window', 'Bakruten din er tom')}</p>
                  <p className="text-3xs text-zinc-600 font-bold max-w-[200px] mt-1 leading-relaxed">
                    {t('stickerBoard.empty_window_hint', 'Drag og plasser klistremerkene dine fra skuffen under på bakruten.')}
                  </p>
                </div>
              )}

              {/* Render Active Stickers */}
              {activeStickers.map(visit => {
                const pos = layout[visit.spotId] || { x: 50, y: 50, rotation: 0, scale: 1 };
                const spot = allAvailableSpots.find(s => s.id === visit.spotId);
                // Use spot fields first, fall back to denormalized municipalityName from the visit
                const muniRaw = spot?.municipality || spot?.property?.kommunenavn
                  || (visit.municipalityName && visit.municipalityName !== 'Fricamp' ? visit.municipalityName : null);
                const muni = muniRaw || null;
                const isActive = activeSticker === visit.spotId;

                // LogoImage inside renderRetroSticker handles all URL fallbacks (coat of arms + campsite logo)

                return (
                  <div
                    key={visit.spotId}
                    onMouseDown={(e) => handleMouseDown(visit.spotId, e)}
                    onTouchStart={(e) => handleTouchStart(visit.spotId, e)}
                    onClick={(e) => { e.stopPropagation(); setActiveSticker(visit.spotId); }}
                    style={{
                      position: 'absolute',
                      left: `${pos.x}%`,
                      top: `${pos.y}%`,
                      transform: `translate(-50%, -50%) rotate(${pos.rotation}deg) scale(${pos.scale})`,
                      cursor: dragRef.current?.spotId === visit.spotId ? 'grabbing' : 'grab',
                      zIndex: isActive ? 50 : 20,
                      transition: dragRef.current?.spotId === visit.spotId ? 'none' : 'transform 0.1s ease-out',
                    }}
                    className={`relative select-none touch-none ${isActive ? 'ring-2 ring-emerald-500 ring-offset-2 ring-offset-zinc-950 rounded-2xl p-0.5' : ''}`}
                  >
                {/* Active control panel overlay */}
                {isActive && (
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-stone-900 border border-stone-700 text-white rounded-xl shadow-2xl p-1 flex items-center gap-1.5 z-[100] animate-in fade-in zoom-in-95 duration-150">
                    <button
                      onClick={(e) => handleRotateSticker(visit.spotId, e)}
                      className="p-1.5 hover:bg-stone-800 text-emerald-400 rounded-lg transition-colors"
                      title={t('stickerBoard.rotate', 'Roter')}
                    >
                      <RotateCw size={11} />
                    </button>
                    <button
                      onClick={(e) => handleScaleSticker(visit.spotId, 0.1, e)}
                      className="p-1 text-2xs font-black hover:bg-stone-800 text-emerald-400 rounded-lg transition-colors w-5 h-5 flex items-center justify-center"
                      title={t('stickerBoard.scale_up', 'Større')}
                    >
                      +
                    </button>
                    <button
                      onClick={(e) => handleScaleSticker(visit.spotId, -0.1, e)}
                      className="p-1 text-2xs font-black hover:bg-stone-800 text-emerald-400 rounded-lg transition-colors w-5 h-5 flex items-center justify-center"
                      title={t('stickerBoard.scale_down', 'Mindre')}
                    >
                      -
                    </button>
                    <div className="w-[1px] h-3 bg-stone-700 mx-0.5" />
                    <button
                      onClick={(e) => handleRemoveSticker(visit.spotId, e)}
                      className="p-1.5 hover:bg-red-950 text-red-400 rounded-lg transition-colors"
                      title={t('stickerBoard.remove', 'Fjern')}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                )}

                {/* renderRetroSticker builds coat-of-arms + campsite logo URL chains internally */}
                {renderRetroSticker(visit, muni !== 'Fricamp' ? muni : null, spot)}
              </div>
            );
          })}
            </div>
          </div>
        </div>

        {/* Avatar-tag — top center of the whole board (outside the scrollable window) */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40 pointer-events-none flex items-center gap-2 bg-stone-900/85 backdrop-blur-sm border border-stone-700/60 rounded-full pl-1 pr-4 py-1 shadow-xl">
          {user.photoURL ? (
            <img
              src={user.photoURL}
              alt=""
              draggable={false}
              className="w-7 h-7 rounded-full object-cover border border-stone-600 shrink-0"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-stone-700 flex items-center justify-center text-stone-200 font-black text-2xs shrink-0">
              {userInitials}
            </div>
          )}
          <span className="text-tiny font-black uppercase tracking-wider text-stone-200 truncate max-w-[200px]">
            {userName}
          </span>
        </div>

        {/* EU license plate — bottom center of the whole board (outside the scrollable window) */}
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 z-40 pointer-events-none flex items-stretch bg-white rounded-t-md shadow-[0_-6px_18px_rgba(0,0,0,0.55)] border-2 border-b-0 border-stone-900 overflow-hidden"
          style={{
            height: '60px',
            minWidth: plateRegnr ? '300px' : '340px',
            fontFamily: '"Arial Black", "Helvetica Neue", Helvetica, Arial, sans-serif',
          }}
        >
          <div className="flex flex-col items-center justify-center bg-[#003399] px-2 py-1">
            <EuStarsRing size={18} />
            <span className="text-white font-black text-[13px] leading-none tracking-tight mt-0.5">
              {plateCountry}
            </span>
          </div>
          <div className="flex items-center justify-center px-4 flex-1">
            {plateRegnr ? (
              <span className="text-black font-black text-[30px] tracking-[0.18em] leading-none whitespace-nowrap">
                {plateRegnr}
              </span>
            ) : (
              <div className="flex items-center gap-2.5 min-w-0">
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt=""
                    draggable={false}
                    className="w-10 h-10 rounded-full object-cover border border-stone-300 shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-stone-200 border border-stone-300 flex items-center justify-center text-stone-700 font-black text-[13px] shrink-0">
                    {userInitials}
                  </div>
                )}
                <span className="text-black font-black text-[16px] tracking-wide leading-tight uppercase truncate max-w-[200px]">
                  {userName}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Klistremerke-skuff (Drawer containing unused stickers) */}
      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl md:rounded-card p-2.5 md:p-3 flex-shrink-0 space-y-1.5">
        <div className="flex items-center justify-between">
          <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)]">
            {t('stickerBoard.drawer_title', 'Dine opplåste klistremerker')}
          </p>
          <span className="bg-emerald-500/10 text-emerald-400 text-4xs font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border border-emerald-500/20">
            {unusedStickers.length} {t('stickerBoard.unused_count', 'ubrukte')}
          </span>
        </div>

        {unusedStickers.length === 0 ? (
          <div className="py-4 text-center border-2 border-dashed border-[var(--border-primary)] rounded-xl flex flex-col items-center justify-center">
            {visits.length === 0 ? (
              <>
                <AlertCircle size={20} className="text-[var(--text-muted)] mb-1.5" />
                <p className="text-2xs font-bold text-[var(--text-muted)] uppercase tracking-wide">
                  {t('stickerBoard.no_stickers_unlocked', 'Ingen klistremerker opplåst')}
                </p>
                <p className="text-3xs text-[var(--text-muted)] max-w-xs px-4 mt-0.5">
                  {t('stickerBoard.unlock_hint', 'Sjekk inn (marker som besøkt) på plasser i kartet for å motta klistremerker.')}
                </p>
              </>
            ) : (
              <>
                <p className="text-2xs font-bold text-emerald-500 uppercase tracking-wide">
                  {t('stickerBoard.all_placed', 'Alle klistremerker er plassert! 🎉')}
                </p>
                <p className="text-3xs text-[var(--text-muted)] max-w-xs px-4 mt-0.5">
                  {t('stickerBoard.all_placed_hint', 'Du har hengt opp alle de {{count}} opplåste klistremerkene dine på bakruten.', { count: visits.length })}
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            <style>{`
              .sticker-drawer-scroll {
                scrollbar-width: thin !important;
                scrollbar-color: rgba(255, 255, 255, 0.35) rgba(0, 0, 0, 0.2) !important;
              }
              .sticker-drawer-scroll::-webkit-scrollbar {
                height: 8px !important;
                width: 8px !important;
                display: block !important;
              }
              .sticker-drawer-scroll::-webkit-scrollbar-track {
                background: rgba(0, 0, 0, 0.2) !important;
                border-radius: 4px !important;
              }
              .sticker-drawer-scroll::-webkit-scrollbar-thumb {
                background: rgba(255, 255, 255, 0.3) !important;
                border-radius: 4px !important;
                border: 1.5px solid transparent !important;
                background-clip: padding-box !important;
              }
              .sticker-drawer-scroll::-webkit-scrollbar-thumb:hover {
                background: rgba(255, 255, 255, 0.5) !important;
              }
            `}</style>
            <div className="flex gap-2.5 overflow-x-auto py-1 px-1 max-h-[110px] md:max-h-[125px] select-none sticker-drawer-scroll" style={{ overscrollBehavior: 'contain' }}>
              {unusedStickers.map(visit => {
                const spot = allAvailableSpots.find(s => s.id === visit.spotId);
                // Use spot fields first, fall back to denormalized municipalityName
                const muniRaw = spot?.municipality || spot?.property?.kommunenavn
                  || (visit.municipalityName && visit.municipalityName !== 'Fricamp' ? visit.municipalityName : null);
                const muni = muniRaw || null;
                // buildCampsiteLogoUrls inside renderRetroSticker handles Clearbit → favicon → photo
                const drawerMuniName = muni !== 'Fricamp' ? muni : null;

                return (
                  <button
                    key={visit.spotId}
                    onClick={() => handleAddSticker(visit.spotId)}
                    className="flex-shrink-0 group relative focus:outline-none focus:scale-105 active:scale-95 transition-all"
                    title={visit.spotName}
                  >
                    <div className="relative">
                      {/* Thumbnail: scaled-down full retro sticker with logo embedded */}
                      <div className="w-14 h-14 flex items-center justify-center overflow-hidden relative select-none">
                        <div className="transform scale-[0.53] origin-center shrink-0 pointer-events-none">
                          {renderRetroSticker(visit, drawerMuniName, spot)}
                        </div>
                      </div>
                      {/* Add hover indicator */}
                      <div className="absolute inset-0 bg-black/40 rounded-xl opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white text-[16px] font-black font-mono leading-none">
                        +
                      </div>
                    </div>
                    <span className="text-[7px] font-bold text-[var(--text-secondary)] block mt-1 max-w-[56px] truncate leading-tight text-center">
                      {visit.spotName}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
